import React, { memo, useCallback, useMemo, useState } from 'react';
import { DataGridPro, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { Box, Menu, MenuItem } from '@mui/material';
import toast from 'react-hot-toast';

import { datagridColors } from '../../../../assets/simpleDatagridStyles';
import { FileUploadDialog } from '../FileUploadDialog';
import { AssignStaffDialog } from '../AssignStaffDialog';
import { useStateContext } from '../../../../hooks/useStateContext';
import { exportToXLSX } from '../../utils/exportToXLSX';
import api from '../../../../services/api';

import { TableProps, TableRow } from './table.types';
import {
  copySelectedRowsToClipboard,
  hasRequiredFields,
  normalizeRows,
} from './table.helpers';
import { buildColumns } from './table.columns';
import { TableToolbar } from './TableToolbar';
import { TableFooter } from './TableFooter';
import {CancelConfirmDialog} from "../CancelConfirmDialog";

export const Table = memo(function Table({
                                           rows,
                                           loading,
                                           onLoading,
                                           onRefresh,
                                           apiRef,
                                           customFields,
                                           onOpenChat,
                                           onEditRow,
                                           onFinalizeRow,
                                           editableFields,
                                           requiredFields,
                                         }: TableProps) {

  const { isMobile, user } = useStateContext();

  const [openConfirmDialog, setOpenConfirmDialog] = useState<TableRow | null>(null);
  const [openAssignedStaffsDialog, setOpenAssignedStaffsDialog] = useState<TableRow | false>(false);
  const [openFileUploadDialog, setOpenFileUploadDialog] = useState<TableRow | false>(false);
  const [openHeaderFilters, setOpenHeaderFilters] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

  const hasMultipleProviders = (user?.service_providers?.length ?? 0) > 1;

  const isFieldRequired = useCallback(
    (fieldKey: string): boolean => {
      if (!requiredFields) return false;

      if (Array.isArray(requiredFields)) {
        return requiredFields.includes(fieldKey);
      }

      return Boolean(requiredFields[fieldKey]);
    },
    [requiredFields]
  );

  const normalizedRows = useMemo(() => normalizeRows(rows), [rows]);

  const columns = useMemo(() => {
    return buildColumns({
      editableFields,
      hasMultipleProviders,
      paginationModel,
      openHeaderFilters,
      setOpenHeaderFilters,
      setOpenConfirmDialog: (row) => setOpenConfirmDialog(row),
      onEditRow,
      onFinalizeRow,
      isFieldRequired,
    });
  }, [
    editableFields,
    hasMultipleProviders,
    paginationModel,
    openHeaderFilters,
    onEditRow,
    onFinalizeRow,
    isFieldRequired,
  ]);

  const handleExport = useCallback(() => {
    exportToXLSX(normalizedRows, columns, apiRef, 'TRAVEL-CITY Foglalások');
  }, [normalizedRows, columns, apiRef]);

  const handleCopy = useCallback(() => {
    toast.promise(copySelectedRowsToClipboard(apiRef, rows), {
      loading: 'Generating clipboard data...',
      success: <b>Copied to clipboard!</b>,
      error: <b>Error while generating data</b>,
    });
  }, [apiRef, rows]);

  const handleRejectConfirmation = useCallback(async (confirmation: TableRow | null) => {

    onLoading(true);
    setLoadingConfirm(true);

    try {
      await api.post(
        `/sales-partner/activity-reservations/${confirmation?.id}/confirmation`,
        {
          action: confirmation?.confirmation_status_id === 2 ? 'decline-confirmed' :'decline',
        }
      );

      onRefresh();
    } catch (error) {
      console.error(error);
    } finally {
      onLoading(false);
      setLoadingConfirm(false);
      setOpenConfirmDialog(null);
    }
  }, [onLoading, onRefresh]);

  const toolbarSlot = useMemo(() => {
    return () => (
      <TableToolbar
        isMobile={isMobile}
        onExport={handleExport}
        onCopy={handleCopy}
      />
    );
  }, [isMobile, handleExport, handleCopy]);

  const fileMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedFiles([]);
  }, []);

  return (
    <>
      <Box sx={datagridColors('calc(100vh - 325px)')}>
        <DataGridPro
          apiRef={apiRef}
          rows={normalizedRows}
          columns={columns}
          loading={loading}
          pagination
          checkboxSelection
          disableColumnMenu
          disableRowSelectionOnClick
          editMode="cell"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[100]}
          unstable_headerFilters={openHeaderFilters}
          initialState={{
            pinnedColumns: { left: ['row_number'], right: ['actions'] },
          }}
          slots={{
            // @ts-ignore
            footer: TableFooter,
            toolbar: toolbarSlot,
            headerFilterMenu: null,
          }}
          columnHeaderHeight={hasRequiredFields(requiredFields) ? 60 : 38}
          getRowClassName={(params) => {
            return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
          }}
          rowHeight={44}
          columnBufferPx={300}
          sx={{
            maxHeight: 'calc(100% - 70px)',
          }}
        />

        {openConfirmDialog && (
          <CancelConfirmDialog
            clickOnReject={() => handleRejectConfirmation(openConfirmDialog)}
            loading={loadingConfirm}
            onClose={() => setOpenConfirmDialog(null)}
          />
        )}

        {openAssignedStaffsDialog && (
          <AssignStaffDialog
            row={openAssignedStaffsDialog}
            onClose={() => setOpenAssignedStaffsDialog(false)}
          />
        )}

        {openFileUploadDialog && (
          <FileUploadDialog
            row={openFileUploadDialog}
            onClose={() => setOpenFileUploadDialog(false)}
          />
        )}

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={fileMenuClose}>
          {selectedFiles.map((file, idx) => {
            const filename = `Fájl ${idx + 1}`;
            const fileUrl = file.link;
            const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fileUrl);
            const isPdf = /\.pdf$/i.test(fileUrl);

            const handleFileClick = () => {
              if (isImage || isPdf) {
                window.open(fileUrl, '_blank');
              } else {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.download = filename;
                link.click();
              }
            };

            return (
              <MenuItem key={idx} onClick={handleFileClick}>
                {filename}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    </>
  );
});